import type { SupportedLocales } from '~~/shared/types/locales'
import { type PREPOSITION_WORD, POINT_OF_INTEREST_TYPE, PREPOSITION_EXCEPTIONS } from '~/constants/point-of-interest'
import type { GrammarRules } from '~/types/grammar-rules'

type PrepositionFn = (args: { word: string, grammarRules: GrammarRules, component?: Nullable<string> }) => string
type ArticleFn = (args: { word: string, grammarRules: GrammarRules, capitalize: boolean }) => string

const prepositionComponentMap: Partial<Record<POINT_OF_INTEREST_TYPE, Record<'nearby' | 'standard', PREPOSITION_WORD>>> = {
  City: { standard: 'in', nearby: 'near' },
  Region: { standard: 'in', nearby: 'in' },
  Country: { standard: 'in', nearby: 'in' },
  Campground: { standard: 'near', nearby: 'near' },
  SuburbanArea: { standard: 'in', nearby: 'near' },
  ThemePark: { standard: 'near', nearby: 'near' },
  Park: { standard: 'near', nearby: 'near' },
}

const articles: Record<SupportedLocales, ArticleFn> = {
  en: () => '',
  fr: ({ word, grammarRules, capitalize }) => {
    word = replaceDiacritics(word)

    const l = capitalize ? 'L' : 'l'
    if (grammarRules.isPluralized) return `${l}es `

    const hasArticleGrammar = grammarRules.isMasculine || grammarRules.isFeminine || grammarRules.isIsland
    if (!hasArticleGrammar) return ''

    if (/^[aeiou]/i.test(word)) return `${l}'`
    if (grammarRules.isMasculine) return `${l}e `
    if (grammarRules.isFeminine) return `${l}a `
    return ''
  },
}

const prepositions: Record<SupportedLocales, Partial<Record<PREPOSITION_WORD, PrepositionFn>>> = {
  en: {
    in: () => 'in ',
    near: () => 'near ',
    from: () => 'from ',
    of: () => 'of ',
  },
  fr: {
    in: ({ word, grammarRules, component }) => {
      word = replaceDiacritics(word)

      if (grammarRules.isIsland) return 'à '

      if (grammarRules.isPluralized) return 'aux '

      if (grammarRules.isFeminine) {
        if (/^etat\b/i.test(word)) return 'dans l\''
        if (/^[aeiou]/i.test(word)) return 'à l\''
        return 'en '
      }

      if (grammarRules.isMasculine) {
        if (/^district\b/i.test(word)) return 'dans le '
        if (component === POINT_OF_INTEREST_TYPE.City) return 'à '
        return 'au '
      }

      return 'à '
    },
    near: ({ word, grammarRules, component }) => {
      word = replaceDiacritics(word)

      if (grammarRules.isIsland) {
        if (/^[aeiou]/i.test(word)) return 'près de l\''
        return 'près du '
      }

      if (grammarRules.isPluralized) return 'près des '

      if (/^[aeiou]/i.test(word)) return 'près d\''

      if (grammarRules.isFeminine) return 'près de la '

      if (grammarRules.isMasculine) {
        if (/^district\b/i.test(word)) return 'dans le '
        if (component === POINT_OF_INTEREST_TYPE.City) return 'près de '
        return 'au '
      }

      return 'près de '
    },
    from: ({ word }) => {
      word = replaceDiacritics(word)

      return /^[aeiou]/i.test(word) ? 'd\'' : 'de '
    },
    of: () => 'de ',
  },
}

export const pointOfInterestLocationName = ({ name, component, parentName }: { name?: Nullable<string>, component?: Nullable<string>, parentName?: Nullable<string> }) => {
  if (!name) {
    return undefined
  }

  const includeParent = component === POINT_OF_INTEREST_TYPE.City || component === POINT_OF_INTEREST_TYPE.Region

  return [name, includeParent ? parentName : undefined].filter(Boolean).join(', ')
}

export const pointOfInterestLocationNameWithPreposition = ({ pointOfInterestId, name, component, parentName, locale, useNearby = false, preposition, grammarRules }: { pointOfInterestId?: Nullable<string>, name?: Nullable<string>, component?: Nullable<string>, parentName?: Nullable<string>, locale: SupportedLocales, useNearby?: boolean, preposition?: PREPOSITION_WORD, grammarRules?: unknown }) => {
  const resolvedPreposition = preposition ?? prepositionComponentMap[component as POINT_OF_INTEREST_TYPE]?.[useNearby ? 'nearby' : 'standard']

  const locationName = pointOfInterestLocationName({ name, component, parentName }) ?? ''

  if (!resolvedPreposition) return locationName

  // Static override for specific POI IDs takes priority over grammar-rule resolution
  const exception = PREPOSITION_EXCEPTIONS[locale]?.[pointOfInterestId ?? '']?.[resolvedPreposition]
  if (exception !== undefined) {
    return `${withTrailingSpace(exception)}${locationName}`
  }

  const prepositionFn = prepositions[locale]?.[resolvedPreposition]
  if (!prepositionFn) return `${resolvedPreposition} ${locationName}`

  const prep = prepositionFn({ word: locationName, grammarRules: parseGrammarRules(grammarRules), component })

  return `${withTrailingSpace(prep)}${locationName}`
}

export const pointOfInterestLocationNameWithArticle = ({ name, locale, capitalize, grammarRules }: { name?: Nullable<string>, locale: SupportedLocales, capitalize: boolean, grammarRules?: unknown }) => {
  const word = pointOfInterestLocationName({ name }) ?? ''
  const resolvedArticle = articles[locale]({ word, grammarRules: parseGrammarRules(grammarRules), capitalize })

  return `${resolvedArticle}${word}`
}

/**
 * Metadata title
 */
const MAX_TITLE_LENGTH = 60
const DEFAULT_SUFFIX = '| RVezy'

function assemble(parts: Array<string | undefined>): string {
  return parts.filter(Boolean).join(' ')
}

/**
 * Assembles an RV rentals meta title. Rating and listing count are only eligible
 * when they meet their quality thresholds. When the title exceeds 60 characters,
 * tokens are dropped in a fixed order until it fits: brand suffix, then rating,
 * then listing count.
 */

export function buildDestinationPageMetadataTitle({
  baseTitle,
  rvsFrom,
  suffix = DEFAULT_SUFFIX,
  avgRating,
  reviewCount,
  rvCount,
}: {
  baseTitle: string
  rvsFrom: string
  suffix?: string
  avgRating: number
  reviewCount: number
  rvCount: number
}): string {
  const MIN_RATING = 4.5
  const MIN_REVIEW_COUNT = 20
  const MIN_RV_COUNT = 8

  const rating = avgRating > MIN_RATING && reviewCount >= MIN_REVIEW_COUNT ? `${avgRating}★,` : undefined
  const count = rvCount >= MIN_RV_COUNT ? String(rvCount) : undefined

  const defaultFallbackParts = [baseTitle, rvsFrom]

  const fallbacks = [
    [baseTitle, rating, count, rvsFrom, suffix],
    [baseTitle, rating, count, rvsFrom],
    [baseTitle, count, rvsFrom],
    defaultFallbackParts,
  ]

  const fitting = fallbacks.find((parts) => assemble(parts).length < MAX_TITLE_LENGTH)

  return assemble(fitting ?? defaultFallbackParts)
}

/**
 * Metadata description
 */
const MAX_DESCRIPTION_LENGTH = 155

export function buildDestinationPageMetadataDescription({
  baseDescription,
  rvCount,
  delivery,
  bookInstantly,
}: {
  baseDescription: string
  rvCount: number
  delivery: string
  bookInstantly: string
}): string {
  const MIN_RV_COUNT = 8

  const count = rvCount >= MIN_RV_COUNT ? String(rvCount) : undefined

  const defaultFallbackParts = [baseDescription]

  const fallbacks = [
    [count, baseDescription, delivery, bookInstantly],
    [count, baseDescription, delivery],
    [count, baseDescription, bookInstantly],
    [count, baseDescription],
    defaultFallbackParts,
  ]

  const fitting = fallbacks.find((parts) => assemble(parts).length < MAX_DESCRIPTION_LENGTH)

  return assemble(fitting ?? defaultFallbackParts)
}
